import { getEmbedSandboxConfig } from 'components/DevPopUp/embedSandbox';

const HAS_URL_SCHEME = /^[a-zA-Z][a-zA-Z\d+.-]*:/;

/**
 * Backend may return only a host (no scheme). Browsers then resolve that string as a
 * path on the current origin, so the iframe loads the parent app instead of the embed.
 */
export const ensureAbsoluteHttpUrl = (value) => {
    if (value == null || typeof value !== 'string') return null;

    const trimmed = value.trim();
    if (!trimmed) return null;

    if (trimmed.startsWith('//')) {
        return `https:${trimmed}`;
    }

    if (HAS_URL_SCHEME.test(trimmed)) {
        return trimmed;
    }

    return `https://${trimmed}`;
};

const isExternalCrudConfig = (value) => {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
};

export const normalizeExternalCrudConfig = (entityConfig) => {
    if (!isExternalCrudConfig(entityConfig)) return null;

    const normalizedId = Number.parseInt(entityConfig.id, 10);

    return {
        ...entityConfig,
        ...(Number.isNaN(normalizedId) ? {} : { id: normalizedId }),
    };
};

export const getExternalCrudConfig = (entityKey, userData = {}) => {
    if (!entityKey) return null;

    const sandboxConfig = getEmbedSandboxConfig(entityKey);
    if (sandboxConfig) return sandboxConfig;

    const entityConfig = userData?.entitiesWithExternalCRUD?.[entityKey];
    return normalizeExternalCrudConfig(entityConfig);
};

export const shouldSkipExternalFormsInit = (entityConfig) => Boolean(entityConfig?.sandbox);

export const supportsExternalCrudAction = (entityConfig, action) => {
    if (!action) return true;

    const actions = entityConfig?.actions;
    if (!Array.isArray(actions)) return true;

    return actions.includes(action);
};

export const hasExternalCrud = (entityKey, userData = {}, action) => {
    const config = getExternalCrudConfig(entityKey, userData);
    if (!config) return false;

    return supportsExternalCrudAction(config, action);
};

export const resolveEmbedCrudMode = (crud) => (crud?.id ? 'edit' : 'create');

export const shouldUseEmbeddedEntityCrud = (entityKey, userData = {}, mode) => {
    return Boolean(entityKey && hasExternalCrud(entityKey, userData, mode));
};

export const getExternalCrudFallbackUrl = (entityKey) => {
    return getEmbedSandboxConfig(entityKey)?.url ?? null;
};

export const resolveExternalCrudIframeUrl = (entityKey, initContext, entityConfig) => {
    return ensureAbsoluteHttpUrl(
        getExternalCrudFallbackUrl(entityKey) || initContext?.url || entityConfig?.url,
    );
};
